import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, authFileUrl } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, hasRole, rolesOf, ROLE_LABELS } from "@/context/AuthContext";
import { toast } from "sonner";
import { PackageCheck, Plus, Check, Loader, Circle, Trash2, Receipt, Camera, Mic, Video as VideoIcon, AlertTriangle, Pencil, X, Sun, Moon } from "lucide-react";

const STAGE_ICON = { done: Check, in_progress: Loader, pending: Circle };
const CATEGORIES = ["Bed", "Dining", "Sofa", "Wardrobe", "Chair", "Table", "Cabinet", "Other"];
const SUP_ROLE_OPTS = [
  { value: "", label: "— no supervisor —" },
  { value: "sup_material", label: "Material Allotment" },
  { value: "sup_manufacturing", label: "Manufacturing" },
  { value: "sup_polish", label: "Sanding & Polishing" },
  { value: "sup_hardware", label: "Final Hardware" },
  { value: "sup_packing", label: "Packing & Labeling" },
  { value: "sup_stuffing", label: "Stuffing" },
];
const uniqCI = (arr) => { const m = new Map(); arr.forEach((v) => { const k = (v || "").toLowerCase(); if (!m.has(k)) m.set(k, v); }); return [...m.values()]; };
const eqCI = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();

export default function Products() {
  const { user } = useAuth();
  const canManage = hasRole(user, "admin", "manager");
  const myRoles = rolesOf(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [form, setForm] = useState({ name: "", range_name: "", category: "Bed", buyer_id: "", sku: "", quantity: 1, order_ref: "" });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [fltBuyer, setFltBuyer] = useState("all");
  const [fltRange, setFltRange] = useState("all");
  const [today, setToday] = useState({ slot: "", stages: [] });
  const [upd, setUpd] = useState(null);
  const [updForm, setUpdForm] = useState(null);
  const [updList, setUpdList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editStages, setEditStages] = useState([]);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  const loadToday = () => api.get("/progress/my-today").then((r) => setToday(r.data || { stages: [] })).catch(() => {});
  useEffect(() => { load(); loadToday(); api.get("/buyers").then((r) => setBuyers(r.data)); }, []);

  const ownsStage = (s) => canManage || (s?.role && myRoles.includes(s.role));
  const todayFor = (pid, idx) => today.stages.find((t) => t.product_id === pid && t.stage_index === idx);
  const myStages = (p) => p.stages.map((s, i) => ({ s, i })).filter(({ s }) => ownsStage(s));

  const uploadTo = async (file, key, multi) => {
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd);
      setUpdForm((p) => ({ ...p, [key]: multi ? [...(p[key] || []), data.path] : data.path }));
    } catch { toast.error("Upload failed"); }
  };
  const openUpdate = async (p) => {
    const ms = myStages(p); if (!ms.length) return;
    setUpd(p); setUpdForm({ stage_index: ms[0].i, photos: [], note: "", voice: "", video: "", shortage: false, shortage_note: "" });
    setUpdList([]);
    try { const { data } = await api.get(`/products/${p.id}/updates`); setUpdList(data || []); } catch (e) { console.warn("Could not load updates", e); toast.error("Couldn't load previous updates"); }
  };
  const submitUpdate = async () => {
    try { const { data } = await api.post(`/products/${upd.id}/update`, updForm);
      toast.success(`${data.slot === "morning" ? "Morning" : "Evening"} update saved`); setUpd(null); load(); loadToday();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (p) => { setEdit(p); setEditStages(p.stages.map((s) => ({ name: s.name, role: s.role || "" }))); };
  const saveEdit = async () => {
    try { await api.patch(`/products/${edit.id}/stages`, { stages: editStages.filter((s) => s.name.trim()) });
      toast.success("Stages updated"); setEdit(null); load(); loadToday();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const buyerOpts = useMemo(() => uniqCI(items.map((p) => p.buyer_name).filter(Boolean)), [items]);
  const rangeOpts = useMemo(() => uniqCI(items.map((p) => p.range_name).filter(Boolean)), [items]);
  const shown = useMemo(() => items.filter((p) => (fltBuyer === "all" || eqCI(p.buyer_name, fltBuyer)) && (fltRange === "all" || eqCI(p.range_name, fltRange))), [items, fltBuyer, fltRange]);

  const openDetail = async (p) => {
    setDetail(null);
    setDetailOpen(true);
    const { data } = await api.get(`/products/${p.id}/summary`);
    setDetail(data);
  };

  const linkCosting = async (cid) => {
    await api.patch(`/products/${detail.product.id}/link-costing`, { costing_id: cid === "none" ? "" : cid });
    const { data } = await api.get(`/products/${detail.product.id}/summary`);
    setDetail(data);
    load();
    toast.success("Cost sheet updated");
  };

  const create = async () => {
    try {
      const b = buyers.find((x) => x.id === form.buyer_id);
      await api.post("/products", { ...form, buyer_name: b?.name || "", quantity: Number(form.quantity) });
      toast.success("Product added to production");
      setOpen(false);
      setForm({ name: "", range_name: "", category: "Bed", buyer_id: "", sku: "", quantity: 1, order_ref: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cycle = async (p, idx) => {
    if (!ownsStage(p.stages[idx])) { toast.error("Only this stage's supervisor can advance it"); return; }
    const cur = p.stages[idx].status;
    const next = cur === "pending" ? "in_progress" : cur === "in_progress" ? "done" : "pending";
    const { data } = await api.patch(`/products/${p.id}/stage`, { index: idx, status: next });
    setItems((prev) => prev.map((x) => (x.id === p.id ? data : x)));
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product removed");
    load();
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Product Progress" subtitle="Track each product from raw wood to dispatch. Tap a stage to advance it." icon={PackageCheck}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px">
                <Plus className="h-4 w-4" /> New Product
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add Product to Production</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Product name</Label>
                  <Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Oak Platform Bed" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Range name</Label>
                    <Input data-testid="product-range" value={form.range_name} onChange={(e) => setForm({ ...form, range_name: e.target.value })} placeholder="Nordic" /></div>
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Buyer</Label>
                    <Select value={form.buyer_id} onValueChange={(v) => setForm({ ...form, buyer_id: v })}>
                      <SelectTrigger data-testid="product-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Product SKU</Label>
                    <Input data-testid="product-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="BED-A100" /></div>
                  <div className="space-y-2"><Label>Quantity</Label>
                    <Input data-testid="product-qty" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Order reference</Label>
                    <Input data-testid="product-order" value={form.order_ref} onChange={(e) => setForm({ ...form, order_ref: e.target.value })} placeholder="EXP-2026-118" /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="product-save" onClick={create} disabled={!form.name}>Add Product</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Select value={fltBuyer} onValueChange={setFltBuyer}>
            <SelectTrigger className="w-full sm:w-52" data-testid="product-filter-buyer"><SelectValue placeholder="All buyers" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All buyers</SelectItem>{buyerOpts.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fltRange} onValueChange={setFltRange}>
            <SelectTrigger className="w-full sm:w-52" data-testid="product-filter-range"><SelectValue placeholder="All ranges" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All ranges</SelectItem>{rangeOpts.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          {(fltBuyer !== "all" || fltRange !== "all") && <Button variant="ghost" size="sm" data-testid="product-filter-clear" onClick={() => { setFltBuyer("all"); setFltRange("all"); }}>Clear</Button>}
        </div>
      )}
      {items.length === 0 ? (
        <EmptyState icon={PackageCheck} title="No products in production" hint="Add your first product to start tracking its journey." />
      ) : shown.length === 0 ? (
        <EmptyState icon={PackageCheck} title="No matches" hint="No products match the selected buyer / range." />
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {shown.map((p) => (
            <Card key={p.id} data-testid={`product-card-${p.id}`} className="animate-fade-up rounded-md border-border p-5 shadow-none">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-lg font-bold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground">{p.sku || "—"} · {p.category || "—"} · {p.quantity} units</p>
                  {(p.buyer_name || p.range_name) && (
                    <p className="text-xs text-muted-foreground">{[p.range_name, p.buyer_name].filter(Boolean).join(" · ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={p.status} />
                  <Button variant="ghost" size="icon" data-testid={`product-delete-${p.id}`} onClick={() => remove(p.id)}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Progress value={p.progress} className="h-2" />
                <span className="text-sm font-bold text-primary">{p.progress}%</span>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Overall cost: </span>
                  <span className="font-heading font-bold">{p.overall_cost ? money(p.overall_cost) : "—"}</span>
                  {p.linked_costing_name
                    ? <span className="ml-2 text-xs text-muted-foreground">via {p.linked_costing_name}</span>
                    : <span className="ml-2 text-xs text-ochre">no cost sheet linked</span>}
                </div>
                <Button size="sm" variant="outline" data-testid={`product-detail-${p.id}`} onClick={() => openDetail(p)} className="gap-1">
                  <Receipt className="h-3.5 w-3.5" /> Cost & Details
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                {myStages(p).length > 0 && (
                  <Button size="sm" data-testid={`daily-update-${p.id}`} onClick={() => openUpdate(p)} className="h-8 gap-1.5">
                    <Camera className="h-3.5 w-3.5" /> Daily Update
                    <span className="ml-1 flex items-center gap-0.5">
                      {["morning", "evening"].map((slot) => {
                        const anyDone = myStages(p).some(({ i }) => todayFor(p.id, i)?.[slot === "morning" ? "morning_done" : "evening_done"]);
                        const I = slot === "morning" ? Sun : Moon;
                        return <I key={slot} className={`h-3.5 w-3.5 ${anyDone ? "text-primary-foreground" : "text-primary-foreground/40"}`} />;
                      })}
                    </span>
                  </Button>
                )}
                {canManage && (
                  <Button size="sm" variant="outline" data-testid={`edit-stages-${p.id}`} onClick={() => openEdit(p)} className="h-8 gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Edit stages
                  </Button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {p.stages.map((s, i) => {
                  const Icon = STAGE_ICON[s.status];
                  const t = todayFor(p.id, i);
                  const styles = s.status === "done" ? "border-sage/40 bg-sage/10 text-sage"
                    : s.status === "in_progress" ? "border-[#4A5D6E]/40 bg-[#4A5D6E]/10 text-[#4A5D6E]"
                    : "border-border bg-card text-muted-foreground";
                  const mine = ownsStage(s);
                  return (
                    <button key={i} data-testid={`stage-${p.id}-${i}`} onClick={() => cycle(p, i)} disabled={!mine}
                      className={`flex flex-col gap-1 rounded-md border px-3 py-2 text-left text-xs font-medium transition-colors duration-200 ${mine ? "hover:border-primary/40" : "cursor-not-allowed opacity-70"} ${styles}`}>
                      <span className="flex items-center gap-2">
                        <Icon className={`h-3.5 w-3.5 shrink-0 ${s.status === "in_progress" ? "animate-spin" : ""}`} />
                        <span className="truncate">{s.name}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        {s.role ? ROLE_LABELS[s.role]?.replace(" Supervisor", "") || s.role : "—"}
                        {t && <Sun className={`h-2.5 w-2.5 ${t.morning_done ? "text-primary" : "text-muted-foreground/30"}`} />}
                        {t && <Moon className={`h-2.5 w-2.5 ${t.evening_done ? "text-primary" : "text-muted-foreground/30"}`} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{detail?.product?.name || "Product"} — Cost & Details</DialogTitle></DialogHeader>
          {!detail ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="max-h-[72vh] space-y-5 overflow-y-auto pr-1">
              <div className="space-y-2">
                <Label>Linked cost sheet {!canManage && <span className="text-xs text-muted-foreground">(view only)</span>}</Label>
                <Select disabled={!canManage} value={detail.product.costing_id || "none"} onValueChange={linkCosting}>
                  <SelectTrigger data-testid="link-costing-select"><SelectValue placeholder="No cost sheet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No cost sheet</SelectItem>
                    {detail.costings_available.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} · {money(c.unit_cost)}/unit</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Overall cost auto-updates from the linked cost sheet and rejected units from quality checks.</p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Stat label="Base unit cost" value={money(detail.product.base_unit_cost)} />
                <Stat label="Planned cost" value={money(detail.product.planned_cost)} sub={`${detail.product.quantity} units`} />
                <Stat label="Rework cost" value={money(detail.product.rework_cost)} sub={`${detail.product.rejected_units} rejected`} />
                <Stat label="Contractor cost" value={money(detail.product.contractor_cost)} sub={`${detail.contractor_orders?.length || 0} orders`} />
                <Stat label="Overall cost" value={money(detail.product.overall_cost)} accent />
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Operations ({detail.operations.length})</p>
                {detail.operations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No operations linked to this product name.</p>
                ) : (
                  <div className="space-y-1.5">
                    {detail.operations.map((o) => (
                      <div key={o.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <span>{o.title}</span><StatusBadge status={o.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.contractor_orders?.length > 0 && (
                <div data-testid="contractor-orders-section">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Contractor Orders ({detail.contractor_orders.length})</p>
                  <div className="space-y-1.5">
                    {detail.contractor_orders.map((o) => (
                      <div key={o.id} data-testid={`contractor-order-${o.id}`} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <span>{o.so_number} · {o.party}{o.product ? ` — ${o.product}` : ""}</span>
                        <span className="flex items-center gap-2"><span className="font-heading font-bold">{money(o.total)}</span><StatusBadge status={o.status} /></span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">Only <b>completed</b> contractor orders add to the overall cost above.</p>
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Quality Checks ({detail.quality_checks.length})</p>
                {detail.qc_summary && detail.quality_checks.length > 0 && (
                  <div className="mb-3 overflow-x-auto rounded-md border border-border" data-testid="qc-summary-table">
                    <div className="grid min-w-[440px] grid-cols-5 bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <span>Level</span><span className="text-right text-sage">Pass</span><span className="text-right text-ochre">Rework</span><span className="text-right text-destructive">Reject</span><span className="text-right">Pending</span>
                    </div>
                    {detail.qc_summary.map((r) => (
                      <div key={r.level} className="grid min-w-[440px] grid-cols-5 border-t border-border px-3 py-1.5 text-sm">
                        <span className="capitalize">{r.level.replace("_", " ")}</span>
                        <span className="text-right font-semibold text-sage">{r.ok}</span>
                        <span className="text-right text-ochre">{r.rework}</span>
                        <span className="text-right text-destructive">{r.reject}</span>
                        <span className="text-right text-muted-foreground">{r.pending}</span>
                      </div>
                    ))}
                  </div>
                )}
                {detail.quality_checks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No quality checks recorded for this product name.</p>
                ) : (
                  <div className="space-y-1.5">
                    {detail.quality_checks.map((q) => (
                      <div key={q.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <span>{q.stage} · {q.quantity_passed} passed / {q.quantity_rejected} rejected</span><StatusBadge status={q.result} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!upd} onOpenChange={(v) => !v && setUpd(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Daily Update — {upd?.name}</DialogTitle></DialogHeader>
          {updForm && (
            <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
              <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                Reporting slot now: <b className="text-foreground">{today.slot === "morning" ? "Morning (till 1 PM)" : "Evening (till 9 PM)"}</b>. Photos are required twice a day.
              </div>
              <div className="space-y-1.5"><Label>Stage</Label>
                <Select value={String(updForm.stage_index)} onValueChange={(v) => setUpdForm({ ...updForm, stage_index: Number(v) })}>
                  <SelectTrigger data-testid="update-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{myStages(upd).map(({ s, i }) => <SelectItem key={i} value={String(i)}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Photos (required)</Label>
                <div className="flex flex-wrap gap-2">
                  {updForm.photos.map((p, i) => (
                    <div key={i} className="relative" data-testid={`update-photo-${i}`}>
                      <img src={authFileUrl(p)} alt="" className="h-16 w-16 rounded border border-border object-cover" />
                      <button type="button" onClick={() => setUpdForm({ ...updForm, photos: updForm.photos.filter((_, x) => x !== i) })} className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-background p-0.5"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded border border-dashed border-primary/50 text-primary hover:bg-primary/5"><Camera className="h-5 w-5" />
                    <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="update-photos-upload" onChange={(e) => { [...e.target.files].forEach((f) => uploadTo(f, "photos", true)); }} /></label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="flex items-center gap-1 text-xs"><Mic className="h-3.5 w-3.5" /> Voice note</Label>
                  {updForm.voice ? <audio controls src={authFileUrl(updForm.voice)} className="h-9 w-full" data-testid="update-voice" /> :
                    <label className="flex cursor-pointer items-center justify-center gap-1 rounded-md border border-dashed border-border px-2 py-2 text-xs hover:border-primary/40"><Mic className="h-3.5 w-3.5" /> Record / upload
                      <input type="file" accept="audio/*" capture className="hidden" data-testid="update-voice-upload" onChange={(e) => e.target.files[0] && uploadTo(e.target.files[0], "voice", false)} /></label>}
                </div>
                <div className="space-y-1.5"><Label className="flex items-center gap-1 text-xs"><VideoIcon className="h-3.5 w-3.5" /> Video</Label>
                  {updForm.video ? <span className="flex items-center gap-1 text-xs text-sage" data-testid="update-video"><Check className="h-3.5 w-3.5" /> attached <button onClick={() => setUpdForm({ ...updForm, video: "" })}><X className="h-3 w-3" /></button></span> :
                    <label className="flex cursor-pointer items-center justify-center gap-1 rounded-md border border-dashed border-border px-2 py-2 text-xs hover:border-primary/40"><VideoIcon className="h-3.5 w-3.5" /> Record / upload
                      <input type="file" accept="video/*" capture="environment" className="hidden" data-testid="update-video-upload" onChange={(e) => e.target.files[0] && uploadTo(e.target.files[0], "video", false)} /></label>}
                </div>
              </div>
              <div className="space-y-1.5"><Label>Note</Label>
                <Textarea rows={2} data-testid="update-note" value={updForm.note} onChange={(e) => setUpdForm({ ...updForm, note: e.target.value })} placeholder="Anything to notice today…" /></div>
              <label className="flex items-center gap-2 rounded-md border border-ochre/40 bg-ochre/10 px-3 py-2 text-sm text-ochre">
                <Checkbox checked={updForm.shortage} onCheckedChange={(v) => setUpdForm({ ...updForm, shortage: !!v })} data-testid="update-shortage" />
                <AlertTriangle className="h-4 w-4" /> Material shortage
              </label>
              {updForm.shortage && <Textarea rows={2} data-testid="update-shortage-note" value={updForm.shortage_note} onChange={(e) => setUpdForm({ ...updForm, shortage_note: e.target.value })} placeholder="What's short? (item, qty)" />}
              {updList.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Recent updates</Label>
                  <div className="max-h-40 space-y-1.5 overflow-y-auto">
                    {updList.slice(0, 6).map((u, i) => (
                      <div key={i} className="rounded-md border border-border px-2 py-1.5 text-xs" data-testid={`update-log-${i}`}>
                        <p className="flex items-center justify-between"><b>{u.stage_name}</b><span className="text-muted-foreground">{u.date} · {u.slot} · {u.created_by_name}</span></p>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {(u.photos || []).slice(0, 4).map((ph, x) => <img key={x} src={authFileUrl(ph)} alt="" className="h-8 w-8 rounded border border-border object-cover" />)}
                          {u.voice && <audio controls src={authFileUrl(u.voice)} className="h-7" />}
                          {u.shortage && <span className="text-ochre">⚠ short</span>}
                        </div>
                        {u.note && <p className="mt-0.5 text-muted-foreground">{u.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="update-submit" onClick={submitUpdate} disabled={!updForm?.photos?.length}>Submit {today.slot === "morning" ? "Morning" : "Evening"} Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit stages — {edit?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
            {editStages.map((s, i) => (
              <div key={i} data-testid={`edit-stage-row-${i}`} className="grid grid-cols-12 items-center gap-2">
                <Input className="col-span-6" data-testid={`edit-stage-name-${i}`} value={s.name} onChange={(e) => setEditStages(editStages.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Stage name" />
                <div className="col-span-5">
                  <Select value={s.role || "none"} onValueChange={(v) => setEditStages(editStages.map((x, j) => (j === i ? { ...x, role: v === "none" ? "" : v } : x)))}>
                    <SelectTrigger data-testid={`edit-stage-role-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{SUP_ROLE_OPTS.map((r) => <SelectItem key={r.value || "none"} value={r.value || "none"}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-muted-foreground hover:text-destructive" data-testid={`edit-stage-del-${i}`} onClick={() => setEditStages(editStages.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="edit-stage-add" onClick={() => setEditStages([...editStages, { name: "", role: "" }])} className="gap-1"><Plus className="h-3.5 w-3.5" /> Add stage</Button>
          </div>
          <DialogFooter><Button data-testid="edit-stages-save" onClick={saveEdit}>Save Stages</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, sub, accent }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`rounded-md border p-3 ${accent ? "border-sage/40 bg-sage/10" : "border-border bg-card"}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 font-heading text-base font-bold ${accent ? "text-sage" : "text-foreground"}`}
      data-testid={`stat-value-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);
